import { grayscale } from 'polished';
import { ThemeParameters, RuntimeThemeParameters } from '../../types';
import { graphDataStyle, graphWallFill } from '../patterns';
import { decreaseContrast, interpolate } from '../util';
import { graphDefault, graphOutlines, graphSkin, graphThreeColorRamp } from './';

export const graphDataNode = {
  nodeDefault(theme: ThemeParameters, parameters: RuntimeThemeParameters) {
    const dataNode = graphDataNode.node1(theme, parameters);
    const color = grayscale(dataNode.color);
    const altColor = grayscale(dataNode.altColor);

    const graphThreeColorRampDefault = graphThreeColorRamp.rampDefault(theme, parameters);

    const common = {
      color,
      fillColor: color,
      altColor,
      lineColor: altColor,
      textColor: dataNode.textColor,
      fillPattern: 'L1',
      fillGradientStartColor: graphThreeColorRampDefault.fillGradientStartColor,
      fillGradientNeutralColor: graphThreeColorRampDefault.fillGradientNeutralColor,
      fillGradientEndColor: graphThreeColorRampDefault.fillGradientEndColor,
    };
    switch (theme.baseThemeName) {
      case 'dark': {
        return {
          ...common,
          lineThickness: graphSkin(theme, parameters).skinGlowThickness,
        };
      }
      case 'high-contrast': {
        return {
          ...common,
          lineThickness: graphOutlines(theme, parameters).lineThickness,
        };
      }
      default: {
        return {
          ...common,
          lineThickness: graphOutlines(theme, parameters).lineThickness,
        };
      }
    }
  },
  node1(theme: ThemeParameters, parameters: RuntimeThemeParameters) {
    return deriveDataNode(theme, parameters, graphDataStyle.style1(theme, parameters));
  },
  node2(theme: ThemeParameters, parameters: RuntimeThemeParameters) {
    return deriveDataNode(theme, parameters, graphDataStyle.style2(theme, parameters));
  },
  node3(theme: ThemeParameters, parameters: RuntimeThemeParameters) {
    return deriveDataNode(theme, parameters, graphDataStyle.style3(theme, parameters));
  },
  node4(theme: ThemeParameters, parameters: RuntimeThemeParameters) {
    return deriveDataNode(theme, parameters, graphDataStyle.style4(theme, parameters));
  },
  node5(theme: ThemeParameters, parameters: RuntimeThemeParameters) {
    return deriveDataNode(theme, parameters, graphDataStyle.style5(theme, parameters));
  },
  node6(theme: ThemeParameters, parameters: RuntimeThemeParameters) {
    return deriveDataNode(theme, parameters, graphDataStyle.style6(theme, parameters));
  },
  node7(theme: ThemeParameters, parameters: RuntimeThemeParameters) {
    return deriveDataNode(theme, parameters, graphDataStyle.style7(theme, parameters));
  },
  node8(theme: ThemeParameters, parameters: RuntimeThemeParameters) {
    return deriveDataNode(theme, parameters, graphDataStyle.style8(theme, parameters));
  },
};

const deriveDataNode = (
  theme: ThemeParameters,
  parameters: RuntimeThemeParameters,
  dataStyle: { color: string; altColor: string }
) => {
  const graphDefaultPattern = graphDefault(theme, parameters);

  switch (theme.baseThemeName) {
    case 'dark': {
      const color = interpolate(dataStyle.color, graphWallFill(theme, parameters).fillColor, 60);
      return {
        color,
        fillColor: color,
        altColor: dataStyle.altColor,
        lineColor: dataStyle.altColor,
        textColor: graphDefaultPattern.n5,
      };
    }
    case 'high-contrast': {
      const color = decreaseContrast(dataStyle.color, graphDefaultPattern.white, 85);
      return {
        color,
        fillColor: color,
        altColor: dataStyle.altColor,
        lineColor: dataStyle.altColor,
        textColor: graphWallFill(theme, parameters).fillColor,
      };
    }
    default: {
      const color = decreaseContrast(
        dataStyle.color,
        graphWallFill(theme, parameters).fillColor,
        60
      );

      return {
        color,
        fillColor: color,
        altColor: dataStyle.altColor,
        lineColor: dataStyle.altColor,
        textColor: graphDefaultPattern.n_3,
      };
    }
  }
};
